import { useState, useEffect, useRef } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from './firebase';
import { useOrderListener } from './hooks/useOrderListener';
import Sidebar, { PAGE_TITLES } from './components/Sidebar';
import TopBar from './components/TopBar';
import { Loading, ToastProvider } from './ui';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Orders from './pages/Orders';
import OrderHistory from './pages/OrderHistory';
import Menu from './pages/Menu';
import Campaigns from './pages/Campaigns';
import Settings from './pages/Settings';
import DeliveryZones from './pages/DeliveryZones';
import Notifications from './pages/Notifications';
import './text-black.css';

function App() {
  const [user, setUser] = useState(undefined);
  const [activePage, setActivePage] = useState('orders');
  const [newOrderCount, setNewOrderCount] = useState(0);
  const [cancelOrderCount, setCancelOrderCount] = useState(0);
  const [navOpen, setNavOpen] = useState(false);

  const [autoPrint, setAutoPrint] = useState(localStorage.getItem('autoPrint') === 'true');
  const [autoAccept, setAutoAccept] = useState(localStorage.getItem('autoAccept') !== 'false');
  const [soundEnabled, setSoundEnabled] = useState(localStorage.getItem('orderSound') !== 'false');

  const autoPrintRef = useRef(autoPrint);
  const soundEnabledRef = useRef(soundEnabled);

  useEffect(() => { autoPrintRef.current = autoPrint; }, [autoPrint]);
  useEffect(() => { soundEnabledRef.current = soundEnabled; }, [soundEnabled]);

  const { reset: resetOrderListener } = useOrderListener({
    user,
    autoPrintRef,
    autoAccept,
    soundEnabledRef,
    onNewOrders: (count) => setNewOrderCount(prev => prev + count),
    onCancelOrders: (count) => setCancelOrderCount(prev => prev + count),
  });

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        // Admin yetkisi ayrı 'admins' koleksiyonundan okunur.
        const adminDoc = await getDoc(doc(db, 'admins', firebaseUser.uid)).catch(() => null);
        if (!adminDoc || !adminDoc.exists()) {
          await signOut(auth);
          setUser(null);
        } else {
          setUser(firebaseUser);
        }
      } else {
        setUser(null);
        setActivePage('orders');
        resetOrderListener();
      }
    });
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (user === undefined) return <div style={{ height: '100vh', display: 'grid', placeItems: 'center' }}><Loading /></div>;
  if (user === null) return <ToastProvider><Login /></ToastProvider>;

  const handlePageChange = (id) => {
    if (id === 'orders') {
      setNewOrderCount(0);
      setCancelOrderCount(0);
    }
    setActivePage(id);
    setNavOpen(false);
  };

  const toggleSound = () => {
    const next = !soundEnabled;
    setSoundEnabled(next);
    localStorage.setItem('orderSound', String(next));
  };

  const totalBadge = newOrderCount + cancelOrderCount;
  const isCancel = cancelOrderCount > 0 && newOrderCount === 0;

  const renderPage = () => {
    switch (activePage) {
      case 'dashboard': return <Dashboard onNavigate={handlePageChange} />;
      case 'orders': return (
        <Orders
          soundEnabled={soundEnabled} setSoundEnabled={setSoundEnabled}
          autoPrint={autoPrint} setAutoPrint={setAutoPrint}
          autoAccept={autoAccept} setAutoAccept={setAutoAccept}
        />
      );
      case 'history': return <OrderHistory />;
      case 'menu': return <Menu />;
      case 'campaigns': return <Campaigns />;
      case 'zones': return <DeliveryZones />;
      case 'notifications': return <Notifications />;
      case 'settings': return <Settings userEmail={user.email} onSignOut={() => signOut(auth)} soundEnabled={soundEnabled} onToggleSound={toggleSound} />;
      default: return <Dashboard onNavigate={handlePageChange} />;
    }
  };

  return (
    <ToastProvider>
      <div className="k-app">
        <Sidebar
          activePage={activePage}
          onPageChange={handlePageChange}
          totalBadge={totalBadge}
          isCancel={isCancel}
          open={navOpen}
        />
        <div className="k-main">
          <TopBar
            title={PAGE_TITLES[activePage] || ''}
            onMenu={() => setNavOpen((o) => !o)}
          />
          <main className="k-content">{renderPage()}</main>
        </div>
      </div>
    </ToastProvider>
  );
}

export default App;
